/**
 * Επεξεργαστής καταλόγου πλατφόρμας — efood (φάση 2β).
 *
 * ΓΙΑΤΙ ξεχωριστό αρχείο και όχι νέα ενότητα στο services/api.ts: το api.ts
 * είναι ~950 γραμμές και το ακουμπούν σχεδόν όλες οι οθόνες — κάθε προσθήκη εκεί
 * γίνεται conflict σε παράλληλη δουλειά (ίδια αιτιολόγηση με το
 * platformMappingApi.ts:1-14). Χρησιμοποιούμε ΤΟ ΙΔΙΟ axios instance (default
 * export του ./api), ώστε να ισχύουν τα interceptors: Bearer token, ανανέωση από
 * x-new-token, 401 → login, 410 → οθόνη μεταφοράς.
 *
 * Συμβόλαιο: docs/platform_name_mapping.md §13.10 (HTTP API) και §13.11 (UI),
 * docs/platform_catalog_contracts.md §6-§7.
 *
 * ΚΑΝΟΝΑΣ ΛΑΘΩΝ (§13.10): τα 409 και τα 403 αυτής της οθόνης ΔΕΝ είναι σφάλματα
 * του χρήστη — είναι κατάσταση του συστήματος («η προεπισκόπηση δεν ισχύει πια»,
 * «η αποστολή είναι κλειδωμένη», «ο επεξεργαστής δεν είναι ενεργός»). Θέλουν
 * ΜΟΝΙΜΟ μήνυμα στην οθόνη, όχι toast 2,5΄΄ που χάνεται πριν διαβαστεί.
 */

import api from './api';

export type PlatformName = 'efood' | 'wolt';
export type CatalogMode = 'off' | 'read' | 'edit';
export type Addressing = 'unknown' | 'code' | 'vendor_ref' | 'our_sku';
export type TargetEnv = 'production' | 'staging';

export type CellState =
  | 'normal' | 'missing' | 'gone' | 'local'
  | 'structure' | 'twin' | 'offer' | 'pending' | 'conflict';

/**
 * Φ5 (§11.8): το `delete` ΔΕΝ είναι «σβήσε το πρόχειρο» — είναι η ενέργεια «αυτό
 * το είδος να μην υπάρχει καθόλου στο efood» (§13.19.Γ). Πριν τη μετάπτωση
 * εξαιρείται από το payload· μετά φεύγει ως `DELETE /catalog`. Το `risk` της
 * γραμμής είναι 'E' — γι' αυτό χρειάστηκε ALTER στο `pce_risk_chk` (migration 228).
 */
export type EditField =
  | 'price' | 'is_available' | 'auto_reset' | 'is_hidden' | 'stock_amount' | 'create' | 'delete';

export type EditStatus =
  | 'draft' | 'queued' | 'sent' | 'verified' | 'unverified'
  | 'failed' | 'conflict' | 'blocked' | 'cancelled';

export type EntityKind = 'item' | 'option' | 'category' | 'new_item';

/** Οι πλατφόρμες του επιλογέα· μόνο το efood έχει υλοποίηση στη φάση 2β. */
export const CATALOG_PLATFORMS: Array<{ value: PlatformName; label: string; enabled: boolean }> = [
  { value: 'efood', label: 'efood', enabled: true },
  { value: 'wolt', label: 'Wolt', enabled: false },
];

/**
 * Το παράθυρο φρέσκου pre-image (§13.6.3). Πάνω από αυτό, ένα κατάστημα με
 * εκκρεμείς αλλαγές δείχνει ΚΟΚΚΙΝΟ τσιπ ηλικίας: ό,τι έκανε ο καταστηματάρχης
 * στο tablet του efood μέσα στο διάστημα αυτό θα επανερχόταν σιωπηλά.
 */
export const IMPORT_FRESH_MINUTES = 10;

// ───────────────────────────── Σύνοψη ─────────────────────────────

export interface StoreSummary {
  store_id: number;
  store_name: string;
  has_credentials: boolean;
  last_import_at: string | null;
  import_age_minutes: number | null;
  items: number;
  unavailable: number;
  write_enabled: boolean;
  addressing: Addressing;
  target_env: TargetEnv;
  open_edits: number;
  twin_groups: number;
}

/**
 * §11.8 — η σύνοψη της Φ5. ΤΑ ΤΡΙΑ ΠΕΔΙΑ ΕΙΝΑΙ ΥΠΟΧΡΕΩΤΙΚΑ ΣΤΟ ΣΥΜΒΟΛΑΙΟ, αλλά
 * η οθόνη δουλεύει και πάνω σε server που δεν τα στέλνει ακόμη — γι' αυτό ό,τι
 * ΔΙΑΒΑΖΕΙ η οθόνη είναι το `StoreSummaryAny` παρακάτω.
 */
export interface StoreSummaryV5 extends StoreSummary {
  migrated_at: string | null;
  last_price_sync_at: string | null;
  price_mismatches: number;
}

/**
 * Ό,τι γυρίζει ΣΗΜΕΡΑ ο server: τα πεδία της Φ5 μπορεί να λείπουν (Φ1-Φ4 είναι
 * ζωντανές και στους δύο παραγωγικούς servers). Κάθε ανάγνωση τους ελέγχει για
 * `undefined` — «δεν ξέρω» ΔΕΝ είναι «δεν έχει μεταπτωθεί».
 */
export type StoreSummaryAny = StoreSummary & Partial<StoreSummaryV5>;

export interface CatalogTotals {
  stores: number;
  with_credentials: number;
  imported: number;
  masters: number;
  open_edits: number;
  conflicts: number;
}

export interface CatalogSummary {
  platform: PlatformName;
  /** Ο διακόπτης χαρακτηριστικού του οργανισμού (§13.12). */
  mode: CatalogMode;
  /** ΑΥΤΟΣ ο server έχει άδεια εξερχόμενης εγγραφής (§13.8.1 πύλες 1-3). */
  server_can_push: boolean;
  /** Συνώνυμο του §13.19.Θ· όποιος server στέλνει μόνο το ένα από τα δύο. */
  push_available?: boolean;
  stores: StoreSummaryAny[];
  totals: CatalogTotals;
}

// ───────────────────────────── Εισαγωγή ─────────────────────────────

export interface ImportStoreResult {
  store_id: number;
  ok: boolean;
  unchanged?: boolean;
  snapshot_id?: number;
  counts?: Record<string, number>;
  changed?: Record<string, Record<string, number>>;
  duration_ms?: number;
  error?: string;
}

export interface ImportReport {
  started_at: string;
  results: ImportStoreResult[];
  totals: { requested: number; ok: number; failed: number; unchanged: number };
}

// ───────────────────────────── Πλέγμα ─────────────────────────────

export interface TwinInfo {
  members: number;
  prices_agree: boolean;
  role: 'main' | 'offer_shadow';
  siblings: string[];
}

export interface PendingEdit {
  edit_id: number;
  field: EditField;
  from: unknown;
  to: unknown;
  /** 'conflict' βάφει το κελί κόκκινο (buildGrid → cellState). */
  status?: EditStatus;
}

export interface GridCell {
  state: CellState;
  item_code?: string;
  price?: number;
  is_available?: boolean;
  auto_reset?: boolean;
  twin?: TwinInfo;
  structure_ok?: boolean;
  in_offer_category?: boolean;
  missing_since?: string | null;
  /**
   * Η ΜΙΑ αλλαγή που ζωγραφίζεται στο κελί (σύγκρουση πρώτα, αλλιώς η πιο
   * πρόσφατη) — ΟΧΙ όλες: ένα κελί μπορεί να έχει ταυτόχρονα πρόχειρη τιμή και
   * πρόχειρη διαθεσιμότητα.
   */
  pending?: PendingEdit | null;
  /** ΟΛΕΣ οι εκκρεμείς αλλαγές του κελιού, μία ανά πεδίο (buildGrid). */
  pending_edits?: PendingEdit[] | null;
}

/**
 * §11.8 — το κελί της Φ5. Το `pending_delete` είναι συντόμευση του backend για
 * «υπάρχει πρόχειρο `field='delete'`»· η οθόνη δέχεται ΚΑΙ αυτό ΚΑΙ σκέτη γραμμή
 * στο `pending_edits` (catalogUi.hasPendingDelete), ώστε να ζωγραφίζει το κόκκινο
 * σήμα είτε ο server στέλνει τη συντόμευση είτε όχι.
 */
export interface GridCellV5 extends GridCell {
  pending_delete?: boolean;
}

export interface GridRow {
  master_key: string;
  display_name: string;
  category_label: string;
  normalized_name: string;
  is_offer_master: boolean;
  stores_present: number;
  stores_total: number;
  price_min: number | null;
  price_max: number | null;
  price_distinct: number;
  unavailable_count: number;
  divergent_structure_count: number;
  /** κλειδί = store_id ΩΣ STRING (έτσι γυρίζει το JSON του backend). */
  cells: Record<string, GridCellV5>;
}

export interface GridStore {
  store_id: number;
  store_name: string;
  /** Σύντομο όνομα στήλης· αν λείπει το φτιάχνει η οθόνη από το store_name. */
  short?: string | null;
  imported_at?: string | null;
}

export interface GridParams {
  platform: PlatformName;
  category?: string;
  q?: string;
  /** Το backend περιμένει 1/0 — ίδια σύμβαση με τα φίλτρα της φάσης 1. */
  only_unavailable?: 1 | 0;
  price_differs?: 1 | 0;
  missing_in?: 1 | 0;
  has_edits?: 1 | 0;
  structure_differs?: 1 | 0;
  twins_only?: 1 | 0;
  hide_offer_items?: 1 | 0;
  limit?: number;
  offset?: number;
}

export interface GridResponse {
  stores: GridStore[];
  rows: GridRow[];
  total: number;
  limit?: number;
  offset?: number;
}

// ───────────────────────────── Αλλαγές ─────────────────────────────

export interface EditChange {
  store_id: number;
  entity_kind: EntityKind;
  item_code?: string;
  tier_code?: string;
  option_code?: string;
  category_code?: string;
  master_key?: string;
  field: EditField;
  /** ΟΤΙ ΕΙΔΕ η οθόνη, όχι ό,τι λέει η βάση τώρα (§13.6.1). */
  old_value: unknown;
  new_value: unknown;
  /** Παρασύρει τα δίδυμα τιμής της ίδιας ομάδας (§13.3.3). */
  apply_to_twins?: boolean;
}

export interface EditRow {
  id: number;
  store_id: number;
  platform_name?: PlatformName;
  master_key?: string | null;
  entity_kind?: EntityKind;
  item_code?: string | null;
  tier_code?: string | null;
  option_code?: string | null;
  category_code?: string | null;
  field: EditField;
  old_value: unknown;
  new_value: unknown;
  /** 'E' = διαγραφή (§13.19.Γ, migration 228: `pce_risk_chk` A|B|C|D|E). */
  risk?: 'A' | 'B' | 'C' | 'D' | 'E';
  status?: EditStatus;
  base_snapshot_id?: number;
  twin_group_key?: string | null;
  created_at?: string;
}

export interface EditsReport {
  created: number;
  updated: number;
  twin_rows: number;
  /** `code` = ο λόγος του backend (not_found | gone | conflict | no_change | twins_disagree…). */
  skipped: Array<{ store_id?: number; item_code?: string; code?: string; reason?: string; message?: string }>;
  edits: EditRow[];
}

// ───────────────────────────── Δίδυμα / προσφορές ─────────────────────────────

export interface TwinGroup {
  twin_key: string;
  store_id?: number;
  members: number;
  prices_agree: boolean;
  codes: string[];
  roles?: Record<string, 'main' | 'offer_shadow'>;
  names?: Record<string, string>;
  prices?: Record<string, number>;
}

/** Προσφορά efood — ΜΟΝΟ ανάγνωση (§13.7 επίπεδο E). */
export interface OfferView {
  platform_code: string;
  title?: string | null;
  is_active?: boolean;
  category_codes?: string[];
  item_codes?: string[];
  /** Τα είδη-δίδυμα της προσφοράς, όπως τα ξέρει το backend. */
  items?: Array<{ item_code: string; name?: string; price?: number | null; is_available?: boolean }>;
  offer_line?: string | null;
  rule?: unknown;
}

export interface DiscountView {
  code?: string | null;
  title?: string | null;
  value?: number | string | null;
  is_active?: boolean;
  raw?: unknown;
}

/**
 * Η απάντηση του `GET …/twins?store_id`.
 *
 * ΓΙΑΤΙ ανέχεται δύο σχήματα: το συμβόλαιο (§7) δηλώνει `Promise<TwinGroup[]>`,
 * αλλά το πάνελ προσφορών/εκπτώσεων του §13.11 χρειάζεται ΚΑΙ τα offers/discounts
 * του καταστήματος και δεν υπάρχει άλλη διαδρομή σε επίπεδο οργανισμού που να τα
 * δίνει. Δεχόμαστε λοιπόν είτε σκέτο πίνακα (τότε το πάνελ δείχνει επεξήγηση ότι
 * ο server δεν τα στέλνει ακόμη) είτε αντικείμενο με τα τρία κλειδιά.
 */
export interface TwinsResponse {
  groups: TwinGroup[];
  offers: OfferView[];
  discounts: DiscountView[];
  /** false όταν ο server γύρισε σκέτο πίνακα διδύμων (παλιότερο συμβόλαιο). */
  has_offers: boolean;
}

// ───────────────────────────── Στεγνή δοκιμή ─────────────────────────────

export interface PlanBlocker {
  code: string;
  store_id?: number;
  item_code?: string;
  message: string;
  /** Τι κάνει η φραγή: πετά ολόκληρο κατάστημα, μία αλλαγή, ή θέλει «ναι». */
  effect?: 'skip_store' | 'skip_edit' | 'confirm' | string;
  requires_ack?: boolean;
}

/**
 * Αλλαγές που ΔΕΝ φεύγουν (§13.6.2). Ο planner τις βγάζει αναλυτικά· χωρίς αυτές
 * στην οθόνη, ένα σχέδιο «22 καταστήματα · 22 αλλαγές» μπορεί να έχει σώματα με
 * 21 γραμμές και ο χειριστής δεν μαθαίνει ποια λείπει ούτε γιατί.
 */
export interface PlanSkipped {
  store_id?: number | null;
  edit_id?: number | null;
  item_code?: string | null;
  field?: EditField | null;
  reason: string;
  message?: string;
}

/** Σύγκρουση ανά γραμμή: η τιμή του efood δεν είναι πια το `old_value`. */
export interface PlanConflict {
  store_id?: number | null;
  edit_id?: number | null;
  item_code?: string | null;
  field?: EditField | null;
  expected?: unknown;
  found?: unknown;
  message?: string;
}

/** store_id → twin_key → «άλλαξέ τα όλα μαζί»· ό,τι δηλώνει το πάνελ διδύμων. */
export type TwinDecisions = Record<string, Record<string, boolean>>;

export interface PlanTwin {
  twin_key: string;
  store_id: number;
  members: number;
  prices_agree: boolean;
  apply_to_all: boolean;
  message: string;
  codes?: string[];
}

export interface PlanCall {
  seq: number;
  method: string;
  endpoint: string;
  url: string;
  chunk?: string;
  rows?: number;
  body?: unknown;
  note?: string;
  changes?: Array<{ item_code: string; name: string; field: EditField; from: unknown; to: unknown; via?: 'twin' }>;
  echoed?: { why: string; fields: string[] };
}

export interface PlanJob {
  seq: number;
  store_id: number;
  store_name: string;
  canary: boolean;
  vendor_id_hash: string;
  target_env: string;
  addressing: Addressing;
  calls: PlanCall[];
  /** Γραμμές διαγραφής (κλήση DELETE μετά τη μετάπτωση) — τις γράφει ο planner από τη Φ5. */
  delete_rows?: number;
}

/** Η ταυτότητα ΕΝΟΣ σχεδίου: ό,τι υπογράφει ο χειριστής όταν πατά «Αποστολή». */
export interface PlanRun {
  mode: 'dry' | 'live';
  platform: PlatformName;
  confirm_hash: string;
  expires_at?: string;
  stores: number;
  edits: number;
  created_at: string;
}

export interface Plan {
  /**
   * ΜΠΟΡΕΙ ΝΑ ΕΙΝΑΙ `null`: ο `price-sync` απαντά ρητά `run: null` όταν καμία
   * τιμή δεν χρειάζεται αλλαγή (§11.7) — δεν υπάρχει τότε ούτε `confirm_hash`
   * ούτε τίποτα να επιβεβαιωθεί. Ο τύπος το λέει ΓΙΑΤΙ ο compiler είναι η μόνη
   * άμυνα: ένα `plan.run.confirm_hash` πάνω σε null ξεμοντάρει ΟΛΟΚΛΗΡΗ την
   * καρτέλα (δεν υπάρχει ErrorBoundary πουθενά στο src/) και θέλει F5.
   */
  run: PlanRun | null;
  blockers: PlanBlocker[];
  twins: PlanTwin[];
  jobs: PlanJob[];
  /** Γραμμές που κόπηκαν πριν γίνουν κλήση (λείπουν από το pre-image, κλειδωμένο πεδίο…). */
  skipped?: PlanSkipped[];
  /** Γραμμές που κόπηκαν επειδή η τιμή του efood άλλαξε. */
  conflicts?: PlanConflict[];
  phase1_effects: Array<{ code: string; message: string; normalized_name?: string; vat?: number }>;
  totals: { http_calls: number; items_touched: number; estimated_seconds: number };
  /** Κίτρινες προειδοποιήσεις — ό,τι δεν φράζει την αποστολή. */
  warnings?: Array<{ code?: string; store_id?: number; message: string }>;
}

/**
 * Σχέδιο ΜΕ ταυτότητα: μόνο αυτό μπαίνει στο παράθυρο επιβεβαίωσης και μόνο
 * αυτό στέλνεται. Ό,τι έρχεται από τον server περνά πρώτα από έλεγχο `run`.
 */
export type SignedPlan = Plan & { run: PlanRun };

// ───────────────────────────── Εκτελέσεις ─────────────────────────────

export interface RunSummary {
  id: number;
  mode: 'dry' | 'live';
  /** Φ5: `migrate` / `delete` / `price_sync` (migration 228, `pcpr_kind_chk`). */
  kind: RunKind;
  status: 'pending' | 'running' | 'done' | 'failed' | 'halted' | 'cancelled';
  created_at: string;
  finished_at?: string | null;
  created_by?: string | null;
  stores_total?: number;
  edits_total?: number;
  target_env?: string;
  halted_reason?: string | null;
  canary_store_id?: number | null;
}

export interface RunJobView {
  id?: number;
  seq: number;
  store_id: number;
  store_name?: string;
  status?: string;
  blocked_reason?: string | null;
  items_before?: number | null;
  items_after?: number | null;
  codes_added?: number | null;
  edits_total?: number | null;
  error?: string | null;
  /**
   * Η αναφορά re-key της μετάπτωσης (`push_jobs.rekey_report`).
   *
   * ΓΙΑΤΙ ΕΔΩ ΚΑΙ ΟΧΙ ΣΤΗΝ ΑΠΑΝΤΗΣΗ ΤΗΣ ΜΕΤΑΠΤΩΣΗΣ: από τη Φ5 το
   * `POST …/migrate` απαντά **202** με μόνο το `run_id` — ο αριθμός των
   * αντιστοιχισμένων, τα ορφανά και τα κενά γράφονται στη γραμμή της
   * εργασίας και ταξιδεύουν ΜΟΝΟ μέσω του `GET …/runs/:id` (`SELECT j.*`).
   * Η μοναδική μη αναστρέψιμη ενέργεια δεν επιτρέπεται να χάνει την αναφορά της.
   */
  rekey_report?: RekeyReport | null;
}

export interface RunCallView {
  id?: number;
  job_id?: number;
  seq: number;
  endpoint: string;
  method?: string;
  url_path?: string;
  chunk?: string | null;
  rows?: number | null;
  http_status?: number | null;
  duration_ms?: number | null;
  outcome?: 'ok' | 'failed' | 'unknown';
  error?: string | null;
}

export interface RunDetail extends RunSummary {
  jobs: RunJobView[];
  calls?: RunCallView[];
  /**
   * `status='running'` αλλά καμία ενημέρωση εργασίας/εκτέλεσης για >30΄ — ο
   * server το παράγει, η οθόνη ΔΕΝ το μαντεύει (§11.9). Σημαίνει «κόπηκε η
   * διεργασία», όχι «απέτυχε η αποστολή».
   */
  interrupted?: boolean;
}

/** Τελικές καταστάσεις: μετά από αυτές δεν αλλάζει τίποτα μόνο του. */
export const TERMINAL_RUN_STATUSES: string[] = ['done', 'failed', 'halted', 'cancelled'];

export const isTerminalRunStatus = (status?: string | null): boolean =>
  !!status && TERMINAL_RUN_STATUSES.indexOf(status) !== -1;

/**
 * Κάθε πόσο ρωτά η οθόνη «πού πήγε η εκτέλεση;» (§11.9).
 *
 * ΓΙΑΤΙ 3΄΄: η αποστολή γράφει μία εργασία ανά κατάστημα με ελάχιστο κενό 1΄΄
 * ανά κλήση και παράθυρο επαλήθευσης 60΄΄ — 3΄΄ δίνουν ζωντανή εικόνα χωρίς να
 * φορτώνουν τη βάση, και 20 καταστήματα × ~1΄ = ~400 ερωτήματα συνολικά.
 */
export const RUN_POLL_MS = 3000;

/**
 * Ό,τι ξέρει η οθόνη για μια εκτέλεση που τρέχει ΣΤΟ ΠΑΡΑΣΚΗΝΙΟ.
 *
 * ΓΙΑΤΙ ΞΕΧΩΡΙΣΤΟ `unreachable`: μια αποτυχία δικτύου στο ερώτημα ΔΕΝ είναι
 * αποτυχία της αποστολής. Η εκτέλεση συνεχίζει να γράφει στο efood· η οθόνη
 * λέει «δες το Ιστορικό», ποτέ «Αποτυχία».
 */
export interface RunProgress {
  run_id: number;
  detail: RunDetail | null;
  unreachable: string | null;
  finished: boolean;
}

// ───────────────────────────── Ρυθμίσεις καταστήματος ─────────────────────────────

export interface StoreSettings {
  store_id: number;
  platform_name?: PlatformName;
  vendor_id?: string | null;
  write_enabled: boolean;
  target_env: TargetEnv;
  addressing: Addressing;
  addressing_note?: string | null;
  probe_status?: string | null;
  probe_at?: string | null;
  last_import_at?: string | null;
  last_push_at?: string | null;
}

// ─────────────── Φ5: τιμές delivery, μετάπτωση, αποστολή (§11.8) ───────────────

/** Η απόφαση του χειριστή ανά κεντρικό είδος. Απουσία γραμμής = `catalog`. */
export type OverrideMode = 'catalog' | 'keep_platform' | 'fixed';

/** Από πού βγαίνει η τιμή που ΘΑ σταλεί (§13.19.Α). */
export type PriceSource = 'catalog' | 'platform';

/** Γιατί ΔΕΝ υπάρχει τιμή καταλόγου για αυτή τη γραμμή (§11.1). */
export type PriceReason =
  | 'unmapped' | 'not_in_store' | 'target_ignore'
  | 'offer_category' | 'no_size_match' | 'missing'
  /** Τιμή καταλόγου <= 0: ΔΕΝ είναι συγκρίσιμη και δεν στέλνεται ποτέ. */
  | 'zero_price'
  /** Υπήρχε απόφαση «σταθερή τιμή» σε είδος με μεγέθη — αγνοείται (ισχύει ο κατάλογος). */
  | 'override_ignored_sized';

export type RunKind = 'push' | 'probe' | 'migrate' | 'delete' | 'price_sync';

/**
 * Μία γραμμή σύγκρισης «τι έχει η πλατφόρμα» ↔ «τι λέει ο κατάλογος (delivery)».
 *
 * Όταν το είδος έχει μεγέθη, έρχεται ΜΙΑ γραμμή ανά επιλογή μεγέθους
 * (`option_code` γεμάτο) — αλλιώς μία γραμμή για το είδος. Ο κανόνας ισότητας
 * είναι `|delta| < 0,005` (NUMERIC → string → float), ποτέ `===`.
 */
export interface PriceDiffRow {
  master_key: string;
  store_id: number;
  item_code: string;
  name: string;
  category_label: string;
  option_code: string | null;
  option_name: string | null;
  platform_price: number | null;
  catalog_price: number | null;
  delta: number | null;
  /** Η ΙΣΧΥΟΥΣΑ απόφαση (προεπιλογή `catalog`, ακόμη κι όταν δεν υπάρχει γραμμή). */
  override_mode: OverrideMode;
  override_note: string | null;
  source: PriceSource;
  reason: PriceReason | null;
  /** `source='catalog'` ΚΑΙ `mode ≠ keep_platform` ΚΑΙ `|delta| ≥ 0,005`. */
  would_send: boolean;
  /**
   * Το είδος τιμολογείται ΜΕΣΩ ΕΠΙΛΟΓΩΝ ΜΕΓΕΘΟΥΣ (§11.7). Τότε «σταθερή τιμή»
   * ΔΕΝ επιτρέπεται: θα έγραφε το ποσό μόνο στο είδος και θα άφηνε τα μεγέθη ως
   * έχουν — δηλαδή ούτε την παλιά ούτε την υποσχεμένη τιμή. Το `PUT
   * …/price-overrides` το απορρίπτει με 400 και η οθόνη δεν το προσφέρει καν.
   */
  is_sized?: boolean;
}

export interface PriceOverride {
  master_key: string;
  mode: OverrideMode;
  fixed_price?: number | null;
  note?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface PriceDiffResponse {
  rows: PriceDiffRow[];
  summary: Record<string, unknown>;
  total: number;
  limit?: number;
  offset?: number;
  /**
   * Ο server έκοψε τη λίστα (§11.1). ΟΣΟ ΕΙΝΑΙ true, η απουσία γραμμής ΔΕΝ
   * σημαίνει «οι τιμές συμφωνούν» — σημαίνει «δεν ήρθε». Το πλέγμα γράφει «—».
   */
  truncated: boolean;
}

export interface MigrationLoss {
  code: string;
  count: number;
  message: string;
}

/**
 * Η στεγνή δοκιμή της μετάπτωσης. ΚΑΜΙΑ εξερχόμενη κλήση — το πλήρες payload
 * (246 KB) ΔΕΝ ταξιδεύει στην οθόνη, μόνο δείγμα στο `preview`.
 */
export interface MigrationPreview {
  store_id: number;
  store_name: string;
  migrated_at: string | null;
  stats: {
    categories: number;
    products: number;
    tiers: number;
    options: number;
    pricelist_rows: number;
    offers: number;
    bytes: number;
    ignore_base_price: string[];
    hidden_categories: string[];
    with_images: number;
    prices_from_catalog: number;
    prices_from_platform: number;
    excluded_by_operator: number;
    system_category_skipped: boolean;
  };
  losses: MigrationLoss[];
  /** Γεμάτος = ΔΕΝ στέλνεται τίποτα (§13.19.Ε.2). */
  integrity: string[];
  blockers: PlanBlocker[];
  preview: { categories: unknown[]; products: unknown[]; offers: unknown[] };
  confirm_hash: string;
  expires_at: string;
  /** Το όνομα που πρέπει να ΠΛΗΚΤΡΟΛΟΓΗΘΕΙ για να τρέξει η μετάπτωση. */
  confirm_text_required: string;
}

export interface MigrationStep {
  step: 'gates' | 'lock' | 'pre_image' | 'build' | 'integrity' | 'ingest'
    | 'poll' | 'import' | 'rekey' | 'settings' | 'id_churn';
  status: 'ok' | 'failed' | 'skipped';
  [k: string]: unknown;
}

export interface RekeyReport {
  status: 'ok' | 'manual_review';
  mapped: number;
  orphans: string[];
  unmatched_new: string[];
  links_moved?: number;
  twins_moved?: number;
  edits_moved?: number;
  tier_gaps?: number;
  option_gaps?: number;
  marked_missing?: number;
}

export interface MigrationResult {
  run_id: number;
  /** Λείπει όσο η εκτέλεση μόλις ξεκίνησε (202) — η εργασία γράφεται πρώτη. */
  job_id?: number | null;
  store_id: number;
  kind: 'migrate';
  /**
   * `accepted` = **202**: η μετάπτωση τρέχει ΣΤΟ ΠΑΡΑΣΚΗΝΙΟ και η οθόνη τη
   * ρωτά με `GET …/runs/:id` (§11.9). ΓΙΑΤΙ: ingest + αναμονή 6-20΄΄ + νέα
   * εισαγωγή + re-key ξεπερνούν το `requestTimeout` του Node (300΄΄), και ένα
   * κομμένο socket ΔΕΝ ακυρώνει τίποτα στο efood — το «Αποτυχία» θα ήταν ψέμα.
   */
  status: 'accepted' | 'pending' | 'running' | 'done' | 'failed' | 'halted' | 'blocked' | 'cancelled';
  steps?: MigrationStep[];
  losses?: MigrationLoss[];
  rekey?: RekeyReport | null;
  error?: string | null;
}

/** Η κεφαλίδα που προσθέτει ο `price-sync` πάνω στο κανονικό Plan (§11.7). */
export interface PriceSyncHeader {
  created: number;
  updated: number;
  skipped_keep_platform: number;
  skipped_no_change: number;
  skipped_conflict: number;
  stores: number;
  [k: string]: number;
}

export type PriceSyncPlan = Plan & {
  sync?: PriceSyncHeader;
  /**
   * Γεμάτο ΜΟΝΟ όταν `run === null`: «Καμία τιμή δεν χρειάζεται αλλαγή…». Είναι
   * η απάντηση που πρέπει να διαβάσει ο χειριστής — όχι άδειο παράθυρο.
   */
  message?: string;
  /** Οι πρόχειρες αλλαγές που δημιουργήθηκαν/ενημερώθηκαν (ενημερωτικά). */
  edit_ids?: number[];
};

/**
 * Η απάντηση του `POST …/push`: **202 «το ανέλαβα»**, ΟΧΙ το αποτέλεσμα.
 *
 * Το `canary_store_id` είναι το κατάστημα που φεύγει ΠΡΩΤΟ: αν δεν επαληθευτεί
 * καθαρά, καμία άλλη εργασία δεν ξεκινά — και είναι αυτό που ο χειριστής
 * παρακολουθεί 24 ώρες (§13.19.ΙΓ β.5). Όταν λείπει, η οθόνη το βγάζει από το
 * ίδιο το σχέδιο (η εργασία με `canary: true`) αντί να μην πει τίποτα.
 */
export interface PushAccepted {
  run_id: number;
  status?: string;
  jobs?: number;
  jobs_planned?: number;
  canary_store_id?: number | null;
}

// ───────── Φ5β: η στήλη efood μέσα στους τιμοκαταλόγους (§13.19.ΙΖ) ─────────

/**
 * ΓΙΑΤΙ ΔΕΥΤΕΡΗ ΠΟΡΤΑ: ο επεξεργαστής καταλόγου (OrgPlatformCatalogTab) είναι
 * εργαλείο ιδιοκτήτη — πλέγμα 179×20, μαζικές ενέργειες, μετάπτωση. Η Κασσιανή
 * που συντηρεί τον κατάλογο ανοίγει ΜΟΝΟ την οθόνη «Τιμοκατάλογοι» κάθε φορά που
 * αλλάζει τιμή. Άρα η τιμή efood πρέπει να φαίνεται ΕΚΕΙ, δίπλα στο Delivery που
 * ήδη συντηρεί — όχι σε άλλη οθόνη που δεν θα μάθει. Η καρτέλα Φ5 ΔΕΝ καταργείται.
 *
 * Το συμβόλαιο είναι του backend: `GET …/price-lists/:priceListId/platform-prices`
 * (routes/orgPlatformCatalog.js §8β + utils/priceListPlatformPrices.js). Ο
 * ΒΑΣΙΚΟΣ τιμοκατάλογος (`stores.price_list_id IS NULL`) ζητείται με το
 * κυριολεκτικό `base` — εκεί ζουν τα 22 από τα 26 καταστήματα των Κουλουράδων.
 */
export type PriceListPlatformState =
  /** Το είδος υπάρχει στο efood και ΟΛΑ τα καταστήματα της ομάδας συμφωνούν. */
  | 'match'
  /** Τα καταστήματα της ίδιας ομάδας ΔΕΝ έχουν την ίδια τιμή efood. */
  | 'differs_between_stores'
  /**
   * ΤΟ ΙΔΙΟ προϊόν είναι καταχωρημένο ΠΑΝΩ ΑΠΟ ΜΙΑ ΦΟΡΑ στο efood ΜΕΣΑ ΣΤΟ ΙΔΙΟ
   * κατάστημα — δύο (ή τρία) κεντρικά είδη, σε ΔΙΑΦΟΡΕΤΙΚΕΣ κατηγορίες.
   *
   * ΔΕΝ ΕΙΝΑΙ ΛΑΘΟΣ, ΕΙΝΑΙ ΤΡΟΠΟΣ ΠΩΛΗΣΗΣ. Μέτρηση στους Κουλουράδες
   * (οργανισμός 2, 19/9): ΔΕΚΑ κανονικοποιημένα ονόματα εμφανίζονται σε πάνω
   * από μία κατηγορία efood, και στα 20 καταστήματα — η «Νηστίσιμα» ξαναγράφει
   * είδη που ζουν και στα «Γλυκά»/«Αλμυρά» (π.χ. «κουλουρι πραλινα νηστισιμη»
   * με ΤΡΙΑ master, 2,30–2,40· «sandwich μεσογειακο με μελιτζανα καπνιστη» με
   * δύο, 2,60–2,70). Σε ΕΙΚΟΣΙ ζεύγη (κατάστημα, όνομα) οι δίδυμες
   * καταχωρήσεις έχουν ΔΙΑΦΟΡΕΤΙΚΗ τιμή· στις υπόλοιπες είναι ίδιες.
   *
   * ΓΙΑΤΙ ΞΕΧΩΡΙΣΤΗ ΚΑΤΑΣΤΑΣΗ: μέχρι σήμερα αυτές οι γραμμές έπεφταν στο
   * `differs_between_stores` και η στήλη έλεγε «διαφωνούν τα καταστήματα» για
   * ΕΝΑ κατάστημα (μετρημένο στο staging: «ΣΟΛΟΜΟΣ», ομάδα ενός καταστήματος,
   * δύο master στο store_id 4 — 14,10 και 13,60). Ψέμα σε ~10 από τις ~179
   * γραμμές κάθε τιμοκαταλόγου.
   */
  | 'duplicate_on_platform'
  /** Το ξέρουμε, αλλά δεν υπάρχει στον κατάλογο efood αυτών των καταστημάτων. */
  | 'absent'
  /** Δεν έχει αντιστοίχιση — υπάρχει όμως ΕΝΑ όνομα efood που του μοιάζει. */
  | 'suggestion';

/** Από πού βγαίνει η τιμή efood της γραμμής. */
export type PriceListPriceBasis = 'item' | 'option' | 'size_options' | null;

/**
 * ΑΠΟ ΠΟΥ ΒΓΗΚΕ Η ΔΙΚΗ ΜΑΣ ΤΙΜΗ DELIVERY που συγκρίθηκε — η στήλη δεν
 * επιτρέπεται να πει «ίδια/διαφέρει» χωρίς να ξέρει ΜΕ ΤΙ.
 *
 * ΓΙΑΤΙ ΥΠΑΡΧΕΙ: μετρημένο στους Κουλουράδες (οργανισμός 2, 246 προϊόντα
 * καταλόγου) — 147 προϊόντα έχουν ΑΚΡΙΒΩΣ ΕΝΑ μέγεθος και ΟΛΑ τους έχουν
 * `org_catalog_products.price_delivery` NULL: η τιμή delivery ζει στο μέγεθος
 * (`price_modifier_delivery` σε 105, εφεδρεία `price_modifier` στα υπόλοιπα 42).
 * Μέχρι χθες η στήλη έπαιρνε μόνο την τιμή του ΠΡΟΪΟΝΤΟΣ, έβρισκε null και
 * έγραφε «χωρίς Delivery» σε ~60% του καταλόγου — τυφλή ακριβώς στη δουλειά για
 * την οποία φτιάχτηκε (μετρημένα: «CHEF» μέγεθος 5,30 vs efood 5,50, «CAESAR'S»
 * 5,30 vs 5,50 — διαφορές 0,20 που δεν φαίνονταν πουθενά).
 *
 *  • `product`     — η τιμή του ίδιου του προϊόντος (η παλιά, μοναδική πηγή),
 *  • `single_size` — το προϊόν έχει ΕΝΑ μέγεθος, άρα το μέγεθος ΕΙΝΑΙ το προϊόν
 *    και η τιμή του δανείζεται στη γραμμή του προϊόντος,
 *  • `size`        — η τιμή της ίδιας της γραμμής μεγέθους,
 *  • `null`        — δεν υπάρχει δική μας τιμή delivery να συγκριθεί.
 */
export type PriceListDeliveryBasis = 'product' | 'single_size' | 'size' | null;

/**
 * ΓΙΑΤΙ ΔΕΝ ΥΠΑΡΧΕΙ ΕΤΥΜΗΓΟΡΙΑ ΕΝΩ ΤΟ EFOOD ΕΧΕΙ ΤΙΜΗ — ώστε η οθόνη να γράψει
 * κουβέντα αντί για σιωπή.
 *
 *  • `multi_size_item_price` — το efood χρεώνει ΜΙΑ τιμή για όλο το είδος ενώ
 *    εμείς τιμολογούμε ανά μέγεθος και τα μεγέθη είναι ΠΑΝΩ ΑΠΟ ΕΝΑ (18
 *    προϊόντα των Κουλουράδων: 12 με δύο, 4 με τρία, 2 με τέσσερα). Δεν υπάρχει
 *    ΕΝΑ δικό μας ποσό να μπει απέναντι — και μαντεψιά εδώ θα χρωμάτιζε κίτρινη
 *    γραμμή που κανείς δεν μπορεί να διορθώσει.
 *  • `no_delivery_price` — δεν τιμολογούμε καθόλου σε delivery.
 */
export type PriceListNoCompareReason = 'multi_size_item_price' | 'no_delivery_price' | null;

/** Η τιμή efood ΕΝΟΣ καταστήματος· έρχεται μόνο όταν λέει κάτι (δες `per_store`). */
export interface PriceListPlatformStorePrice {
  store_id: number;
  price: number | null;
  is_available?: boolean | null;
  /** Το όνομα του είδους ΣΤΟ efood αυτού του καταστήματος. */
  name?: string | null;
  master_key?: string | null;
}

/**
 * Η ΜΙΑ πρόταση αντιστοίχισης· τη βγάζει ο `rankCandidates` της φάσης 1 με το
 * ΙΔΙΟ φράγμα εμπιστοσύνης — καμία νέα ομοιότητα.
 *
 * Το `mapping_id` είναι η γραμμή του `platform_name_mappings`: ΧΩΡΙΣ αυτό δεν
 * υπάρχει τίποτα να ενημερωθεί με το υπάρχον `PUT …/platform-mappings/:id` και η
 * οθόνη ΔΕΝ γράφει με άλλο τρόπο (καμία δεύτερη διαδρομή για την ίδια δουλειά).
 */
export interface PriceListPlatformSuggestion {
  normalized_name: string;
  /** Το όνομα όπως το γράφει το efood — αυτό διαβάζει ο άνθρωπος. */
  display_name: string;
  mapping_id: number | null;
  /** Σε πόσα καταστήματα της ομάδας εμφανίζεται αυτό το όνομα. */
  stores: number;
  min_price: number | null;
  max_price: number | null;
  score?: number;
  kind?: 'exact' | 'fuzzy' | 'history';
  /** Ο ΦΠΑ του προϊόντος μας ≠ ο προεπιλεγμένος της πλατφόρμας (§ φάση 1). */
  vat_risk?: boolean;
}

/**
 * ΜΙΑ καταχώρηση efood μιας γραμμής `duplicate_on_platform` — ένα κεντρικό είδος
 * (`master_key`) με τη ΔΙΚΗ ΤΟΥ κατηγορία και τη ΔΙΚΗ ΤΟΥ τιμή.
 *
 * ΓΙΑΤΙ ΤΑΞΙΔΕΥΕΙ ΟΛΟΚΛΗΡΗ: όταν η γραμμή κρύβει δύο master, το `master_key`
 * είναι `null` και το πάνελ ΔΕΝ μπορεί να γράψει απόφαση (θα έγραφε και στα δύο
 * είδη το ίδιο, ή σε τίποτα). Με τις καταχωρήσεις ξεχωριστά, η απόφαση γίνεται
 * ΑΝΑ ΚΑΤΑΧΩΡΗΣΗ: «το νηστίσιμο κράτα το όπως είναι, το γλυκό πάρε τη δική μας».
 *
 * Το `category_label` είναι ΑΝΘΡΩΠΙΝΟ κείμενο («Νηστίσιμα»), όχι το θραύσμα του
 * master_key («νηστισιμα») — αλλιώς η οθόνη θα ζητούσε από την Κασσιανή να
 * διαβάσει κλειδιά βάσης.
 */
export interface PriceListPlatformDuplicateEntry {
  master_key: string;
  /** Η κατηγορία efood όπως τη γράφει το efood· `null` όταν δεν τη βρήκαμε. */
  category_label: string | null;
  /** Το όνομα του είδους ΣΤΟ efood (μπορεί να διαφέρει ανά καταχώρηση). */
  name?: string | null;
  /** Η συμφωνημένη τιμή της καταχώρησης· `null` όταν διαφέρει ανά κατάστημα. */
  price: number | null;
  min_price?: number | null;
  max_price?: number | null;
  /** Σε πόσα καταστήματα ΤΗΣ ΟΜΑΔΑΣ υπάρχει αυτή η καταχώρηση. */
  stores: number;
  /** ΟΛΑ τα καταστήματα την έχουν ανοιχτή; (= `unavailable_stores === 0`). */
  is_available?: boolean;
  /** Σε πόσα από αυτά είναι ΤΩΡΑ μη διαθέσιμη. */
  unavailable_stores?: number;
  /** Σε πόσα δεν έχει καθόλου τιμή — «0,00 δεν είναι τιμή». */
  stores_without_price?: number;
  /** Η αποθηκευμένη απόφαση ΑΥΤΗΣ της καταχώρησης· απουσία = «κατάλογος». */
  override?: PriceOverride | null;
}

/**
 * Μία γραμμή του τιμοκαταλόγου όπως τη βλέπει το efood. Ταυτότητα: `org_size_id`
 * όταν πρόκειται για μέγεθος, αλλιώς `org_product_id` — ίδιο κλειδί γραμμής με
 * το πλέγμα τιμών (OrgPriceListsManager: `ek()`).
 */
export interface PriceListPlatformRow {
  kind: 'product' | 'size';
  org_product_id: number;
  org_size_id: number | null;
  name: string;
  state: PriceListPlatformState;
  /** Η ΙΣΧΥΟΥΣΑ τιμή delivery της ομάδας (override → κατάλογος → βάση). */
  delivery_price: number | null;
  /** Η συμφωνημένη τιμή efood· `null` όταν τα καταστήματα διαφωνούν. */
  efood_price: number | null;
  min_price: number | null;
  max_price: number | null;
  /** `null` = δεν συγκρίνεται (λείπει ένα από τα δύο ποσά), ΟΧΙ «ίδια». */
  same_as_delivery: boolean | null;
  stores_with_item: number;
  /** Ποια καταστήματα της ομάδας ΔΕΝ το έχουν στο efood. */
  missing_store_ids: number[];
  /** Σε πόσα καταστήματα της ομάδας είναι ΤΩΡΑ μη διαθέσιμο. */
  unavailable_stores: number;
  price_basis: PriceListPriceBasis;
  /**
   * Με ΤΙ συγκρίθηκε το `delivery_price` (δες `PriceListDeliveryBasis`).
   * Προαιρετικό στους τύπους: server χωρίς τη διόρθωση της 19/9/2026 δεν το
   * στέλνει και η οθόνη μένει σιωπηλή γι' αυτό, αντί να μαντέψει.
   */
  delivery_price_basis?: PriceListDeliveryBasis;
  /** Ποιο μέγεθος έδωσε την τιμή — σε `single_size` το ΕΝΑ, σε `size` το ίδιο της. */
  delivery_price_size_id?: number | null;
  /** Γιατί δεν υπάρχει ετυμηγορία ενώ το efood έχει τιμή. */
  no_compare_reason?: PriceListNoCompareReason;
  /**
   * ΓΡΑΜΜΗ-ΚΑΘΡΕΦΤΗΣ: μέγεθος που δείχνει τα κελιά efood ΤΟΥ ΓΟΝΕΑ του, επειδή
   * το efood τιμολογεί το ΕΙΔΟΣ και το είδος έχει ΕΝΑ μέγεθος (147 από τα 246
   * προϊόντα των Κουλουράδων). Ίδια τιμή, ίδια ετυμηγορία — ΤΟ ΙΔΙΟ γεγονός, όχι
   * δεύτερο: δεν μετριέται δεύτερη φορά στη «δουλειά» (δες
   * `isPlatformRowNoteworthy` και το `needsAttention` του backend).
   */
  mirrors_product?: boolean;
  /** `null` όταν η γραμμή κρύβει ΠΟΛΛΑ κεντρικά είδη (δες `master_keys`). */
  master_key: string | null;
  master_keys: string[];
  /** Τιμολογείται μέσω επιλογών μεγέθους → «σταθερή τιμή» απορρίπτεται (400). */
  is_sized: boolean;
  /** Η αποθηκευμένη απόφαση· απουσία = η προεπιλογή «κατάλογος». */
  override: PriceOverride | null;
  /** `null` σημαίνει ΡΗΤΑ «όλα τα καταστήματα ίδια», όχι «δεν ξέρω». */
  per_store: PriceListPlatformStorePrice[] | null;
  suggestion: PriceListPlatformSuggestion | null;
  /**
   * ΜΟΝΟ σε `state='duplicate_on_platform'`: μία εγγραφή ανά είδος efood. Το
   * `master_key` της γραμμής μένει `null` (δεν υπάρχει ΕΝΑ), οι αποφάσεις
   * γράφονται από εδώ.
   */
  duplicate_entries?: PriceListPlatformDuplicateEntry[] | null;
  /**
   * Πόσες φορές, ΤΟ ΠΟΛΥ, μέσα σε ΕΝΑ κατάστημα (0/1 = καμία διπλοεγγραφή).
   * ΑΥΤΟΣ είναι ο αριθμός που βλέπει ο πελάτης — οι καταχωρήσεις μπορεί να
   * είναι περισσότερες δια καταστημάτων.
   */
  duplicate_max_per_store?: number;
  /** Σε πόσα καταστήματα της ομάδας συμβαίνει η διπλοεγγραφή. */
  duplicate_stores?: number;
  /** ΜΕΣΑ στο ίδιο κατάστημα, οι διπλοεγγραφές έχουν ΔΙΑΦΟΡΕΤΙΚΗ τιμή; */
  duplicate_prices_differ?: boolean;
  /**
   * Κάποια κελιά έχουν τιμή ΚΑΙ κάποια όχι — «0,00 δεν είναι δωρεάν, είναι
   * άγνωστη τιμή». Σε κάθε ΑΛΛΗ κατάσταση αυτό βγάζει τη γραμμή
   * `differs_between_stores`· στη διπλοεγγραφή ταξιδεύει ως δικό του πεδίο,
   * γιατί αλλιώς το κατάστημα με το 0,00 θα κρυβόταν πίσω από τις δίδυμες που
   * συμφωνούν και θα έπεφτε έξω από το «Μόνο διαφορές efood».
   */
  duplicate_price_gap?: boolean;
  /**
   * Η γραμμή είναι ΚΑΙ διπλοεγγραφή ΚΑΙ ασυμφωνία καταστημάτων. Κερδίζει η
   * διπλοεγγραφή (είναι η αιτία· χωρίς να τη λύσεις δεν ξέρεις καν ΠΟΙΑ τιμή
   * συγκρίνεις ανά κατάστημα), αλλά η δεύτερη πληροφορία ΔΕΝ χάνεται: το πάνελ
   * τη γράφει και δείχνει το per-store.
   */
  also_differs_between_stores?: boolean;
}

/**
 * ΟΙ ΜΕΤΡΗΤΕΣ ΚΑΤΑΣΤΑΣΗΣ ΕΙΝΑΙ ΓΡΑΜΜΕΣ, ΟΧΙ ΠΡΟΪΟΝΤΑ — και η διαφορά είναι ×2.
 * Τα `match` / `differs_between_stores` / `duplicate_on_platform` / `absent` /
 * `suggestion` αθροίζουν ΑΚΡΙΒΩΣ σε `rows`. Μέσα στις `rows` όμως υπάρχει η
 * ΓΡΑΜΜΗ-ΚΑΘΡΕΦΤΗΣ (`PriceListPlatformRow.mirrors_product`): σε προϊόν με ΕΝΑ
 * μέγεθος — 147 από τα 246 των Κουλουράδων — βγαίνει γραμμή και για το μέγεθος,
 * με τα ΙΔΙΑ κελιά και την ΙΔΙΑ κατάσταση με τον γονέα της. Άρα ένα μονομεγέθες
 * προϊόν που ταιριάζει δίνει `match: 2`, ένα διπλοεγγεγραμμένο
 * `duplicate_on_platform: 2`. Για «πόσα ΠΡΟΪΟΝΤΑ» αφαίρεσε το `mirror_rows`.
 * ΟΙ ΜΕΤΡΗΤΕΣ ΔΟΥΛΕΙΑΣ ΔΕΝ ΧΡΕΙΑΖΟΝΤΑΙ ΑΦΑΙΡΕΣΗ: `needs_attention`,
 * `differs_from_delivery` και `with_unavailable` αγνοούν ήδη τον καθρέφτη.
 */
export interface PriceListPlatformSummary {
  rows: number;
  /** ΓΡΑΜΜΕΣ σε συμφωνία — με τον καθρέφτη μέσα (βλ. `mirror_rows`). */
  match: number;
  /** ΓΡΑΜΜΕΣ όπου τα καταστήματα διαφωνούν — με τον καθρέφτη μέσα. */
  differs_between_stores: number;
  /**
   * ΓΡΑΜΜΕΣ με ΠΑΝΩ ΑΠΟ ΜΙΑ καταχώρηση efood μέσα στο ίδιο κατάστημα — με τον
   * καθρέφτη μέσα. Τα ΠΡΟΪΟΝΤΑ είναι `duplicate_on_platform − mirror_rows` μόνο
   * αν όλοι οι καθρέφτες είναι διπλοεγγραφές· αλλιώς μέτρα τις ίδιες τις `rows`
   * με `!r.mirrors_product`.
   */
  duplicate_on_platform: number;
  /** ΓΡΑΜΜΕΣ εκτός efood. Ο καθρέφτης ΔΕΝ βγαίνει εδώ (δεν υπάρχει γεγονός). */
  absent: number;
  /** ΓΡΑΜΜΕΣ με πρόταση ονόματος. Ούτε εδώ βγαίνει καθρέφτης. */
  suggestion: number;
  /** ΜΕΤΡΗΤΗΣ ΔΟΥΛΕΙΑΣ — χωρίς καθρέφτες, ΜΙΑ φορά ανά πραγματική διαφορά. */
  differs_from_delivery: number;
  /** ΜΕΤΡΗΤΗΣ ΔΟΥΛΕΙΑΣ — χωρίς καθρέφτες. */
  with_unavailable: number;
  /** Ο αριθμός δίπλα στο «Μόνο διαφορές efood» — τον μετρά ο server, χωρίς καθρέφτες. */
  needs_attention: number;
  /**
   * Πόσες από τις `rows` είναι καθρέφτες — Ο ΑΦΑΙΡΕΤΕΟΣ. Χωρίς αυτόν τον αριθμό
   * κανείς έξω από τον server δεν μπορεί να γυρίσει τις γραμμές σε προϊόντα.
   */
  mirror_rows: number;
}

export interface PriceListPlatformStore {
  store_id: number;
  store_name: string;
  has_catalog: boolean;
}

export interface PriceListPlatformResponse {
  platform: PlatformName;
  /** Ο διακόπτης του οργανισμού· 'read' = στήλη ναι, ενέργειες όχι (§13.12). */
  mode: CatalogMode;
  price_list_id: number | null;
  stores: PriceListPlatformStore[];
  stores_with_catalog: number;
  /**
   * Κανένα κατάστημα της ομάδας δεν έχει εισαγμένο κατάλογο → η στήλη ΔΕΝ
   * εμφανίζεται καθόλου. ΓΙΑΤΙ όχι το `stores.delivery_efood`: η σημαία είναι
   * TRUE σε 17 από τα 26 καταστήματα ενώ κατάλογο έχουν 20 — λέει ψέματα προς
   * τις δύο κατευθύνσεις.
   */
  available: boolean;
  /** Καμία σελιδοποίηση: η ΑΠΟΥΣΙΑ γραμμής σημαίνει `default_state`. */
  complete: boolean;
  default_state: PriceListPlatformState;
  rows: PriceListPlatformRow[];
  summary: PriceListPlatformSummary;
  /** Ονόματα efood που δεν δένουν με κανένα προϊόν — δουλειά της άλλης οθόνης. */
  suggestions_left: number;
  /** Ο server λέει ΤΙ ΕΠΙΤΡΕΠΕΤΑΙ, ώστε η οθόνη να μην υποσχεθεί κουμπί με 403. */
  can_decide: boolean;
  can_map: boolean;
}

/** Σύνοψη «δεν ήρθε τίποτα» — ποτέ `undefined` στη θέση αριθμού. */
export const EMPTY_PLATFORM_SUMMARY: PriceListPlatformSummary = {
  rows: 0, match: 0, differs_between_stores: 0, duplicate_on_platform: 0,
  absent: 0, suggestion: 0,
  differs_from_delivery: 0, with_unavailable: 0, needs_attention: 0,
  mirror_rows: 0,
};

/** Ίδια ταυτότητα γραμμής με το `ek()` του πλέγματος τιμών. */
export const priceListPlatformKey = (r: PriceListPlatformRow): string | null => {
  if (r.org_size_id != null) return `s${Number(r.org_size_id)}`;
  if (r.org_product_id != null) return `p${Number(r.org_product_id)}`;
  return null;
};

/** Τα κεντρικά είδη πίσω από τη γραμμή — ΕΚΕΙ γράφεται η απόφαση τιμής. */
export const rowMasterKeys = (r: PriceListPlatformRow | null | undefined): string[] => {
  if (!r) return [];
  const many = Array.isArray(r.master_keys) ? r.master_keys.filter(Boolean).map(String) : [];
  if (many.length > 0) return many;
  return r.master_key ? [String(r.master_key)] : [];
};

/**
 * «Ίδια τιμή;» — ΤΟ ΙΔΙΟ φράγμα με το backend (`PRICE_EPSILON` = 0,005): NUMERIC
 * → string → float, ποτέ `===`.
 */
export const PLATFORM_PRICE_EPSILON = 0.005;

/** Οι καταχωρήσεις efood μιας διπλοεγγραφής — ποτέ `undefined` στη θέση λίστας. */
export const rowDuplicateEntries = (
  r: PriceListPlatformRow | null | undefined
): PriceListPlatformDuplicateEntry[] => {
  if (!r || !Array.isArray(r.duplicate_entries)) return [];
  return r.duplicate_entries.filter(e => e && e.master_key != null);
};

/**
 * ΠΟΣΕΣ ΦΟΡΕΣ ΤΟ ΒΛΕΠΕΙ Ο ΠΕΛΑΤΗΣ σε ΕΝΑ κατάστημα — όχι πόσες καταχωρήσεις
 * υπάρχουν σε όλη την ομάδα. Τρία master δια καταστημάτων μπορεί να σημαίνουν
 * δύο εμφανίσεις ανά κατάστημα, και «3 φορές στο efood» θα ήταν λάθος για κάθε
 * συγκεκριμένο μαγαζί. Χωρίς το πεδίο πέφτουμε στις καταχωρήσεις και μετά στα
 * `master_keys`: λιγότερο ακριβές, ποτέ ψεύτικο.
 */
export const rowDuplicateCount = (r: PriceListPlatformRow | null | undefined): number => {
  const perStore = Number(r && r.duplicate_max_per_store);
  if (Number.isFinite(perStore) && perStore > 1) return perStore;
  const entries = rowDuplicateEntries(r);
  return entries.length > 0 ? entries.length : rowMasterKeys(r).length;
};

/**
 * ΤΟ EFOOD ΧΡΕΩΝΕΙ ΣΤΟ ΕΙΔΟΣ, ΟΧΙ ΑΝΑ ΜΕΓΕΘΟΣ (`price_basis: 'item'` — 148 από
 * τις 169 γραμμές προϊόντος των Κουλουράδων· μόνο 13 είναι `size_options`).
 *
 * ΓΙΑΤΙ ΤΟ ΧΡΕΙΑΖΕΤΑΙ Η ΟΘΟΝΗ: σε τέτοιο είδος ο ΜΟΝΟΣ αριθμός efood είναι
 * αυτός της γραμμής του προϊόντος — τα μεγέθη από κάτω δεν έχουν δικό τους και
 * μέχρι χθες έγραφαν «—», δηλαδή «δεν υπάρχει στο efood». Και σε γραμμή
 * μεγέθους η ίδια τιμή σημαίνει «καθρέφτης του προϊόντος», όχι τιμή επιλογής.
 */
export const rowPricesItemLevel = (r: PriceListPlatformRow | null | undefined): boolean =>
  !!r && (r.price_basis === 'item' || r.mirrors_product === true);

/**
 * «Θέλει μάτι»: διαφορά τιμής, ασυμφωνία καταστημάτων, ή πρόταση αντιστοίχισης.
 * ΑΚΡΙΒΩΣ το κατηγόρημα `needsAttention` του backend
 * (utils/priceListPlatformPrices.js) — αν αποκλίνει, ο μετρητής θα λέει άλλον
 * αριθμό από όσες γραμμές μένουν όταν πατηθεί το φίλτρο.
 *
 * Η ΔΙΠΛΟΕΓΓΡΑΦΗ ΜΟΝΗ ΤΗΣ ΔΕΝ ΕΙΝΑΙ ΔΟΥΛΕΙΑ. Στους Κουλουράδες είναι 10 ονόματα
 * × 20 καταστήματα, αλλά μόνο 20 ζεύγη (κατάστημα, όνομα) έχουν ΟΝΤΩΣ
 * διαφορετική τιμή: αν έμπαιναν όλα στο «Μόνο διαφορές efood», το φίλτρο θα
 * σταματούσε να δείχνει αυτό για το οποίο φτιάχτηκε. Μπαίνει μόνο όταν υπάρχει
 * διαφορά ΤΙΜΗΣ να αποφασιστεί — ΤΑ ΤΕΣΣΕΡΑ ΙΔΙΑ σκέλη με το backend
 * (priceListPlatformPrices.js `needsAttention`):
 *  (α) οι διπλοεγγραφές ΕΝΟΣ καταστήματος δεν συμφωνούν (`duplicate_prices_differ`),
 *  (β) τα καταστήματα δεν συμφωνούν μεταξύ τους (`also_differs_between_stores`),
 *  (γ) κάποιο κατάστημα έχει το είδος ΧΩΡΙΣ τιμή ενώ άλλα το χρεώνουν
 *      (`duplicate_price_gap`) — η διπλοεγγραφή δεν ακυρώνει το «0,00 δεν είναι
 *      τιμή», αλλιώς το κατάστημα που το χαρίζει εξαφανίζεται από τον έλεγχο,
 *  (δ) η μία συμφωνημένη τιμή τους διαφέρει από τη ΔΙΚΗ ΜΑΣ Delivery.
 * Η διπλοεγγραφή που συμφωνεί εξακολουθεί να ΦΑΙΝΕΤΑΙ στη γραμμή της — απλώς
 * δεν χτυπά καμπανάκι.
 */
export const isPlatformRowNoteworthy = (r: PriceListPlatformRow | null | undefined): boolean => {
  if (!r) return false;
  // Ο ΚΑΘΡΕΦΤΗΣ ΔΕΝ ΕΙΝΑΙ ΔΕΥΤΕΡΗ ΔΟΥΛΕΙΑ — ίδιο σκέλος με το backend
  // (priceListPlatformPrices.js `needsAttention`). Η γραμμή του μοναδικού
  // μεγέθους δείχνει τα κελιά ΤΟΥ ΓΟΝΕΑ: αν η «CHEF» διαφέρει 5,30 ≠ 5,50,
  // διαφέρει ΜΙΑ φορά. Μετρημένη και δεύτερη, ο αριθμός δίπλα στο «Μόνο
  // διαφορές efood» θα διπλασιαζόταν για 147 προϊόντα και θα έπαυε να σημαίνει
  // «τόσα πράγματα έχεις να δεις».
  if (r.mirrors_product === true) return false;
  if (r.state === 'differs_between_stores' || r.state === 'suggestion') return true;
  if (r.state === 'duplicate_on_platform') {
    return r.duplicate_prices_differ === true
      || r.also_differs_between_stores === true
      || r.duplicate_price_gap === true
      || r.same_as_delivery === false;
  }
  return r.state === 'match' && r.same_as_delivery === false;
};

// ───────────────────────────── Λάθη ─────────────────────────────

/** Το ελληνικό μήνυμα του backend πριν πέσουμε σε γενικό κείμενο της οθόνης. */
export const catalogErrorText = (e: any): string => (
  (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) || ''
);

const statusOf = (e: any): number => (e && e.response && Number(e.response.status)) || 0;

/**
 * 409 = κατάσταση, όχι λάθος χρήστη: «η προεπισκόπηση δεν ισχύει πια»,
 * «εκτελείται ήδη εισαγωγή», «η τιμή άλλαξε στο efood από τότε που άνοιξες την
 * οθόνη». ΜΟΝΙΜΟ μήνυμα, ποτέ toast (§13.10).
 */
export const isCatalogConflict = (e: any): boolean => statusOf(e) === 409;

/** 403 = πύλη (διακόπτης off, λάθος ρόλος, κλειδωμένο πεδίο επιπέδου C). */
export const isCatalogForbidden = (e: any): boolean => statusOf(e) === 403;

export const isCatalogRouteMissing = (e: any): boolean => {
  const s = statusOf(e);
  return s === 404 || s === 501;
};

export interface CatalogNotice {
  kind: 'ok' | 'warn' | 'err';
  text: string;
}

/**
 * Ενιαία μετάφραση σφάλματος σε μόνιμο μήνυμα οθόνης. Τα 409/403 βγαίνουν
 * ΚΙΤΡΙΝΑ (κατάσταση) και όχι κόκκινα (βλάβη), με το κείμενο του backend αυτούσιο
 * — αυτό λέει στον χειριστή τι ακριβώς να κάνει.
 */
export const catalogNotice = (e: any, fallback: string): CatalogNotice => ({
  kind: (isCatalogConflict(e) || isCatalogForbidden(e)) ? 'warn' : 'err',
  text: catalogErrorText(e) || fallback,
});

// ───────────────────────────── Ο client ─────────────────────────────

/** Κανονικοποίηση της απάντησης των διδύμων (δες TwinsResponse). */
const normalizeTwins = (data: any): TwinsResponse => {
  if (Array.isArray(data)) {
    return { groups: data as TwinGroup[], offers: [], discounts: [], has_offers: false };
  }
  const obj = (data && typeof data === 'object') ? data : {};
  const groups = Array.isArray(obj.groups) ? obj.groups
    : (Array.isArray(obj.twins) ? obj.twins : []);
  const hasOffers = Array.isArray(obj.offers) || Array.isArray(obj.discounts);
  return {
    groups: groups as TwinGroup[],
    offers: Array.isArray(obj.offers) ? obj.offers : [],
    discounts: Array.isArray(obj.discounts) ? obj.discounts : [],
    has_offers: hasOffers,
  };
};

export const orgPlatformCatalogAPI = {
  getSummary: async (orgId: number, platform: PlatformName): Promise<CatalogSummary> => {
    const response = await api.get(`/organizations/${orgId}/platform-catalog/summary`, { params: { platform } });
    return response.data;
  },

  /**
   * Εισαγωγή (GET στο efood) — ΔΕΝ περνά από το interlock εγγραφής, άρα
   * επιτρέπεται και σε server που δεν είναι εκδότης. Χωρίς `storeIds` το backend
   * εισάγει όλα τα καταστήματα με διαπιστευτήρια.
   */
  runImport: async (orgId: number, platform: PlatformName, storeIds?: number[]): Promise<ImportReport> => {
    const body: Record<string, unknown> = { platform };
    if (storeIds && storeIds.length > 0) body.store_ids = storeIds;
    const response = await api.post(`/organizations/${orgId}/platform-catalog/import`, body);
    return response.data;
  },

  getGrid: async (orgId: number, params: GridParams): Promise<GridResponse> => {
    const response = await api.get(`/organizations/${orgId}/platform-catalog/grid`, { params });
    return response.data;
  },

  getMaster: async (orgId: number, platform: PlatformName, masterKey: string): Promise<any> => {
    const response = await api.get(
      `/organizations/${orgId}/platform-catalog/masters/${encodeURIComponent(masterKey)}`,
      { params: { platform } }
    );
    return response.data;
  },

  /**
   * `include_discounts=1` ΠΑΝΤΑ: οι εκπτώσεις δεν έχουν πίνακα (§13.3.2), ζουν
   * μέσα στο στιγμιότυπο και το backend τις διαβάζει μόνο όταν ζητηθούν ρητά.
   * Χωρίς αυτή την παράμετρο η ενότητα «Εκπτώσεις» του πάνελ είναι μονίμως άδεια
   * — δηλαδή ακριβώς η ερώτηση που έρχεται να απαντήσει ο χειριστής («γιατί
   * βλέπω έκπτωση στο efood;») μένει αναπάντητη.
   */
  getTwins: async (orgId: number, platform: PlatformName, storeId: number): Promise<TwinsResponse> => {
    const response = await api.get(`/organizations/${orgId}/platform-catalog/twins`, {
      params: { platform, store_id: storeId, include_discounts: 1 },
    });
    return normalizeTwins(response.data);
  },

  createEdits: async (orgId: number, platform: PlatformName, changes: EditChange[]): Promise<EditsReport> => {
    const response = await api.post(`/organizations/${orgId}/platform-catalog/edits`, { platform, changes });
    return response.data;
  },

  /**
   * Το axios θέλει το σώμα του DELETE μέσα σε `{ data }` — χωρίς αυτό φεύγει
   * κενό αίτημα και το backend δεν βλέπει ποτέ το `all`.
   */
  deleteEdits: async (
    orgId: number,
    platform: PlatformName,
    sel: { ids?: number[]; all?: boolean; store_id?: number }
  ): Promise<{ deleted: number }> => {
    const response = await api.delete(`/organizations/${orgId}/platform-catalog/edits`, {
      data: { platform, ...sel },
    });
    return response.data;
  },

  /**
   * `twin_decisions` = οι αποφάσεις του πάνελ διδύμων (store_id → twin_key →
   * «μαζί/χώρια»). Ο planner τις δέχεται (`buildPlan({ twinDecisions })`)· αν ο
   * server αυτής της έκδοσης δεν τις προωθεί ακόμη, το σχέδιο γυρίζει με τις
   * προεπιλογές — γι' αυτό η οθόνη ΣΥΓΚΡΙΝΕΙ το `apply_to_all` που γύρισε με ό,τι
   * ζήτησε και το λέει, αντί να αφήνει τον χειριστή να νομίζει ότι αποφάσισε.
   */
  dryRun: async (
    orgId: number,
    platform: PlatformName,
    sel: { edit_ids?: number[]; all?: boolean; twin_decisions?: TwinDecisions }
  ): Promise<Plan> => {
    const response = await api.post(`/organizations/${orgId}/platform-catalog/dry-run`, { platform, ...sel });
    return response.data;
  },

  /**
   * ΦΑΣΗ 5: ΠΡΑΓΜΑΤΙΚΗ αποστολή (§13.19.Θ — ήταν 409 στις Φ1-Φ4).
   *
   * Απαντά **202** μόλις γραφτεί η γραμμή της εκτέλεσης και συνεχίζει στο
   * παρασκήνιο: 20 καταστήματα × (κλήσεις + 60΄΄ επαλήθευση) δεν χωρούν σε ένα
   * αίτημα HTTP. Η πρόοδος διαβάζεται με `GET …/runs/:id` κάθε 3΄΄.
   *
   * Το `confirm_hash` είναι ΤΟΥ ΣΧΕΔΙΟΥ που είδε ο άνθρωπος: αν στο μεταξύ
   * άλλαξε οτιδήποτε, το backend απαντά 409 `confirm_stale` και η οθόνη δείχνει
   * ΜΟΝΙΜΟ μήνυμα — ποτέ σιωπηλή επανάληψη πάνω σε άλλο σχέδιο.
   */
  push: async (
    orgId: number,
    platform: PlatformName,
    body: { edit_ids?: number[]; all?: boolean; confirm_hash: string }
  ): Promise<PushAccepted> => {
    const response = await api.post(`/organizations/${orgId}/platform-catalog/push`, { platform, ...body });
    return response.data;
  },

  listRuns: async (orgId: number, platform: PlatformName, limit = 20): Promise<RunSummary[]> => {
    const response = await api.get(`/organizations/${orgId}/platform-catalog/runs`, {
      params: { platform, limit },
    });
    const data = response.data;
    return Array.isArray(data) ? data : ((data && data.runs) || []);
  },

  getRun: async (orgId: number, runId: number): Promise<RunDetail> => {
    const response = await api.get(`/organizations/${orgId}/platform-catalog/runs/${runId}`);
    const data = response.data || {};
    if (!data.run) return data as RunDetail;
    return {
      ...data.run,
      jobs: data.jobs || [],
      calls: data.calls || [],
      // Το `interrupted` το ΠΑΡΑΓΕΙ ο server (status='running' χωρίς ενημέρωση
      // >30΄). Δεχόμαστε και τις δύο θέσεις: ρίζα ή μέσα στο `run`.
      interrupted: data.interrupted === true || data.run.interrupted === true,
    };
  },

  cancelRun: async (
    orgId: number, runId: number
  ): Promise<{ status: string; message?: string; jobs_cancelled?: number }> => {
    const response = await api.post(`/organizations/${orgId}/platform-catalog/runs/${runId}/cancel`);
    return response.data;
  },

  putStoreSettings: async (
    orgId: number,
    storeId: number,
    body: Partial<StoreSettings> & { platform?: PlatformName }
  ): Promise<StoreSettings> => {
    const response = await api.put(`/organizations/${orgId}/platform-catalog/stores/${storeId}/settings`, body);
    return response.data;
  },

  /**
   * Λίστα εργασίας ανά κατάστημα σε CSV. Κατεβαίνει μέσω axios (και όχι με
   * σκέτο <a href>) επειδή η διαδρομή θέλει Bearer token — ένα απλό link θα
   * γύριζε 401 σε νέο tab.
   */
  worklistCsv: async (orgId: number, platform: PlatformName, storeId: number): Promise<Blob> => {
    const response = await api.get(`/organizations/${orgId}/platform-catalog/worklist.csv`, {
      params: { platform, store_id: storeId },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  // ──────────────────── Φ5: τιμές, μετάπτωση (§11.7) ────────────────────

  /**
   * Σύγκριση «τιμή efood» ↔ «τιμή καταλόγου (delivery)» (§13.19.Α).
   *
   * ΚΑΜΙΑ εξερχόμενη κλήση: διαβάζει το τελευταίο στιγμιότυπο. Τα `only_mismatch`
   * και τα λοιπά διακοπτάκια ταξιδεύουν ως 1/0 — ίδια σύμβαση με το πλέγμα.
   * Χωρίς `store_id` γυρίζει ΟΛΑ τα καταστήματα, με `store_id` σε κάθε γραμμή.
   */
  getPriceDiff: async (
    orgId: number,
    p: { platform: PlatformName; store_id?: number; only_mismatch?: boolean; limit?: number; offset?: number }
  ): Promise<PriceDiffResponse> => {
    const params: Record<string, unknown> = { platform: p.platform };
    if (p.store_id !== undefined && p.store_id !== null) params.store_id = p.store_id;
    params.only_mismatch = p.only_mismatch ? 1 : 0;
    if (p.limit !== undefined) params.limit = p.limit;
    if (p.offset !== undefined) params.offset = p.offset;
    const response = await api.get(`/organizations/${orgId}/platform-catalog/price-diff`, { params });
    const data = response.data || {};
    const rows: PriceDiffRow[] = Array.isArray(data.rows) ? data.rows : [];
    const total = Number(data.total) || 0;
    return {
      rows,
      summary: (data.summary && typeof data.summary === 'object') ? data.summary : {},
      total,
      limit: data.limit,
      offset: data.offset,
      // Ο server της Φ5 το λέει ρητά· ο παλιότερος όχι — τότε το συμπεραίνουμε
      // από τα νούμερα. ΠΟΤΕ προεπιλογή `false`: θα επέτρεπε στο πλέγμα να
      // γράψει «ίδια» για γραμμές που κόπηκαν.
      truncated: data.truncated === true || total > rows.length,
    };
  },

  /** Οι ΓΡΑΜΜΕΣ-ΑΠΟΦΑΣΕΙΣ. Ό,τι λείπει από εδώ σημαίνει `catalog` (§13.19.Β). */
  getPriceOverrides: async (orgId: number, platform: PlatformName): Promise<PriceOverride[]> => {
    const response = await api.get(`/organizations/${orgId}/platform-catalog/price-overrides`, {
      params: { platform },
    });
    const data = response.data;
    if (Array.isArray(data)) return data as PriceOverride[];
    return (data && Array.isArray(data.overrides)) ? data.overrides : [];
  },

  /**
   * Αποθήκευση αποφάσεων. `mode:'catalog'` **ΣΒΗΝΕΙ** τη γραμμή (επιστροφή στην
   * προεπιλογή) — δεν κρατάμε 3.694 γραμμές θορύβου που θα έκρυβαν τις 20
   * πραγματικές αποφάσεις.
   */
  putPriceOverrides: async (
    orgId: number, platform: PlatformName, overrides: PriceOverride[]
  ): Promise<{ upserted: number; deleted: number; skipped: Array<{ master_key: string; code: string }> }> => {
    const response = await api.put(`/organizations/${orgId}/platform-catalog/price-overrides`, {
      platform, overrides,
    });
    const data = response.data || {};
    return {
      upserted: Number(data.upserted) || 0,
      deleted: Number(data.deleted) || 0,
      skipped: Array.isArray(data.skipped) ? data.skipped : [],
    };
  },

  /**
   * Δημιουργεί πρόχειρες αλλαγές τιμής για κάθε γραμμή με `would_send` και
   * γυρίζει ΠΛΗΡΕΣ σχέδιο (στεγνή δοκιμή). ΚΑΜΙΑ εξερχόμενη κλήση.
   * Τα είδη με `keep_platform` ΔΕΝ παράγουν ποτέ γραμμή.
   */
  priceSync: async (
    orgId: number, p: { platform: PlatformName; store_ids?: number[] }
  ): Promise<PriceSyncPlan> => {
    const body: Record<string, unknown> = { platform: p.platform };
    if (p.store_ids && p.store_ids.length > 0) body.store_ids = p.store_ids;
    const response = await api.post(`/organizations/${orgId}/platform-catalog/price-sync`, body);
    return response.data;
  },

  /** Στεγνή δοκιμή μετάπτωσης — ΚΑΜΙΑ εξερχόμενη κλήση (§11.7). */
  migrateDryRun: async (
    orgId: number, p: { platform: PlatformName; store_id: number }
  ): Promise<MigrationPreview> => {
    const response = await api.post(`/organizations/${orgId}/platform-catalog/migrate/dry-run`, p);
    return response.data;
  },

  /**
   * Η ΜΗ ΑΝΑΣΤΡΕΨΙΜΗ μετάπτωση ενός καταστήματος (§13.19.Ε).
   *
   * Το `confirm_text` πρέπει να είναι ΑΚΡΙΒΩΣ το όνομα του καταστήματος: είναι η
   * μόνη ανθρώπινη πύλη πριν από ολική αντικατάσταση μενού που δεν αναιρείται.
   * Κρατάμε μεγάλο timeout — η κλήση περιλαμβάνει ingest + polling 6-20΄΄ +
   * εισαγωγή + re-key, και ένα πρόωρο timeout του client ΔΕΝ ακυρώνει τίποτα
   * στο efood (θα έδειχνε «απέτυχε» ενώ το μενού έχει ήδη αντικατασταθεί).
   */
  migrate: async (
    orgId: number,
    p: { platform: PlatformName; store_id: number; confirm_hash: string; confirm_text: string }
  ): Promise<MigrationResult> => {
    const response = await api.post(`/organizations/${orgId}/platform-catalog/migrate`, p, {
      timeout: 300000,
    });
    return response.data;
  },
  /**
   * Η στήλη efood ΤΟΥ ΤΙΜΟΚΑΤΑΛΟΓΟΥ (Φ5β). `priceListId = 'base'` = η ομάδα των
   * καταστημάτων χωρίς τιμοκατάλογο (`stores.price_list_id IS NULL`).
   *
   * ΜΙΑ ΚΛΗΣΗ ΑΝΑ ΟΜΑΔΑ, όχι ανά κατάστημα: το backend απαντά με σταθερό αριθμό
   * ερωτημάτων, ανεξάρτητο από τα 18 καταστήματα και τις 179 γραμμές.
   *
   * ΚΑΜΙΑ σελιδοποίηση (`complete: true`): η ΑΠΟΥΣΙΑ γραμμής σημαίνει
   * `default_state` ('absent'), όχι «δεν ήρθε». Η οθόνη ΠΡΕΠΕΙ να το διαβάζει
   * έτσι, αλλιώς 800 προϊόντα θα έδειχναν «δεν ξέρω» αντί για «δεν υπάρχει εκεί».
   */
  getPriceListPlatformPrices: async (
    orgId: number,
    priceListId: number | 'base',
    platform: PlatformName
  ): Promise<PriceListPlatformResponse> => {
    const response = await api.get(
      `/organizations/${orgId}/price-lists/${priceListId}/platform-prices`,
      { params: { platform } }
    );
    const d = response.data || {};
    return {
      platform,
      // «Δεν ξέρω» ΔΕΝ είναι «επεξεργασία»: πέφτουμε σε 'read', δηλαδή η στήλη
      // φαίνεται αλλά καμία απόφαση δεν αποθηκεύεται κατά λάθος.
      mode: (d.mode === 'off' || d.mode === 'read' || d.mode === 'edit') ? d.mode : 'read',
      price_list_id: d.price_list_id != null ? Number(d.price_list_id) : null,
      stores: Array.isArray(d.stores) ? d.stores : [],
      stores_with_catalog: Number(d.stores_with_catalog) || 0,
      available: d.available === true,
      complete: d.complete !== false,
      default_state: d.default_state || 'absent',
      rows: Array.isArray(d.rows) ? d.rows : [],
      summary: (d.summary && typeof d.summary === 'object') ? d.summary : EMPTY_PLATFORM_SUMMARY,
      suggestions_left: Number(d.suggestions_left) || 0,
      // Χωρίς ΡΗΤΟ ναι, κανένα κουμπί: καλύτερα «δεν προσφέρεται» παρά 403 αφού
      // ο χειριστής έχει ήδη πάρει την απόφαση.
      can_decide: d.can_decide === true,
      can_map: d.can_map === true,
    };
  },
};

export default orgPlatformCatalogAPI;
